import { SEMAPHORE, type SemaphoreStatus } from './colors'
import { MONTH_LABELS, periodLabel, sentenceCase } from './periodLabels'
import type { Currency } from '@/types/common'
import type { ContractBudget } from '@/types/contracts'

const CURRENCY_UNIT_MAP: Record<string, Currency> = {
  '$': 'CLP',
  CLP: 'CLP',
  pesos: 'CLP',
  UF: 'UF',
  USD: 'USD',
  'US$': 'USD',
}

// Formulas whose computed value is a percentage (budget progress / consumption),
// NOT a monetary amount. Their Valor must never be formatted as currency even
// if a currency unit was set by mistake. Mirror of BUDGET_FORMULAS in the model.
const BUDGET_FORMULAS = ['budget_ytd', 'budget_progress', 'budget_lifetime']

export function isCurrencyUnit(unit: string | null | undefined): boolean {
  return !!unit && unit in CURRENCY_UNIT_MAP
}

export function currencyFromUnit(unit: string): Currency {
  return CURRENCY_UNIT_MAP[unit] ?? 'CLP'
}

function isBudgetFormula(valueFormula: string | null | undefined): boolean {
  return !!valueFormula && BUDGET_FORMULAS.includes(valueFormula)
}

// A KPI's measured value is monetary when its unit maps to a known currency AND
// it is not a budget-formula KPI (those compute a percentage). Detection relies
// on the structured unit map and the value_formula flag, never on free-text.
export function isCurrencyKpiValue(
  unit: string | null | undefined,
  valueFormula: string | null | undefined,
): boolean {
  return isCurrencyUnit(unit) && !isBudgetFormula(valueFormula)
}

// La tabla de meses vive en utils/periodLabels.ts, en minuscula, que es la
// forma que se usa dentro de una frase (OPSENG-3090). Aca solo se deriva la
// capitalizada que estos formateadores ya devolvian: dos tablas paralelas eran
// lo que dejaba "Logro · Agosto 2026" conviviendo con "vs julio".
const MONTH_NAMES = MONTH_LABELS.map(sentenceCase)

const SHORT_MONTH_NAMES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
]

export function formatCurrency(
  value: number | null | undefined,
  currency: Currency = 'CLP',
): string {
  if (value == null) return '-'

  switch (currency) {
    case 'CLP':
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value)
    case 'UF': {
      const formatted = new Intl.NumberFormat('es-CL', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
      return `UF ${formatted}`
    }
    case 'USD':
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
  }
}

export function formatNumber(
  value: number | null | undefined,
  currency: Currency = 'CLP',
): string {
  if (value == null || !Number.isFinite(value)) return '-'
  const locale = currency === 'USD' ? 'en-US' : 'es-CL'
  const isInt = Number.isInteger(value)
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: isInt ? 0 : 2,
    maximumFractionDigits: isInt ? 0 : 2,
  }).format(value)
}

// A reajuste factor (e.g. 1,0125). Always at least 3 decimals so factors read as
// factors and not as plain numbers, and at most 6 - the precision the índices are
// published with.
export function formatFactor(factor: number | null | undefined): string {
  if (factor == null || !Number.isFinite(factor)) return '-'
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 6,
  }).format(factor)
}

// A factor as its percentage variation (1,0125 → "+1,25%"). The sign is always
// explicit - a reajuste that lowers the PU must read as a fall at a glance - and
// a fall uses a real minus sign (U+2212), not a hyphen.
export function formatPercentDelta(factor: number | null | undefined): string {
  if (factor == null || !Number.isFinite(factor)) return '-'
  const percent = (factor - 1) * 100
  const formatted = new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 4,
  }).format(Math.abs(percent))
  return `${percent < 0 ? '−' : '+'}${formatted}%`
}

// El valor de un KPI sin su unidad. Lo usan las vistas que ya declaran la unidad
// una sola vez (la columna Unidad de la tabla por ambito, el chip del portal, el
// encabezado del grafico): repetirla en cada celda desalinea los numeros y hace
// mas dificil compararlos entre filas. El simbolo de moneda si se conserva - es
// parte de como se lee un monto, no la unidad repetida.
export function formatKpiNumber(
  value: number | null | undefined,
  unit: string | null | undefined,
  valueFormula?: string | null,
): string {
  if (value == null || !Number.isFinite(value)) return '-'

  if (isCurrencyKpiValue(unit, valueFormula)) {
    return formatCurrency(value, currencyFromUnit(unit!))
  }

  const isInt = Number.isInteger(value)
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: isInt ? 0 : 1,
    maximumFractionDigits: isInt ? 0 : 2,
  }).format(value)
}

// El valor con la unidad pegada. Lo siguen usando las vistas fuera del alcance de
// OPSENG-3010 (configuracion de KPIs, carga de mediciones, planes de accion).
// Ojo: KpiTableRow y MeasurementRow tienen su propia columna Unidad y ademas
// repiten el sufijo en Meta/PC - el mismo defecto que esta card corrigio en la
// tabla por ambito. Migrarlas a formatKpiNumber es trabajo pendiente.
export function formatKpiValue(
  value: number | null | undefined,
  unit: string | null | undefined,
  valueFormula?: string | null,
): string {
  const formatted = formatKpiNumber(value, unit, valueFormula)

  if (formatted === '-') return formatted
  if (!unit || isCurrencyKpiValue(unit, valueFormula)) return formatted
  if (unit === '%') return `${formatted}%`
  return `${formatted} ${unit}`
}

// es-CL como el resto de los numeros de la app: `toFixed` imprimia el score con
// punto decimal ("100.3") al lado de valores medidos con coma ("2,45"), que era
// LA inconsistencia de formato de la vista de desempeno.
export function formatScore(score: number | string | null | undefined): string {
  if (score == null) return '-'
  const n = Number(score)
  if (isNaN(n)) return '-'
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(n)
}

// Un logro SIEMPRE lleva su "%" explicito (regla 9 del PRD de Desempeno) y "sin
// dato" se escribe con raya, nunca con un 0 que se confunda con un logro real.
// Acepta string porque los decimales del backend viajan serializados asi.
export function formatAchievement(score: number | string | null | undefined): string {
  if (score == null) return '—'
  return `${formatScore(score)} %`
}

// Porcentaje generico (pesos, porciones) en es-CL: entero sin decimales, no
// entero con 1-2. Para logros usar formatAchievement, que fija 1 decimal.
export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '-'
  return `${formatKpiNumber(value, null)} %`
}

// La formula de una regla de reajuste en una linea («30 % fijo + 70 % IPC»).
// Unica fuente: antes ReajusteRuleCard y ReajusteRuleDrawer la armaban a mano,
// cada uno con su propio formato de porcentaje.
export function reajusteFormulaLabel(
  fixedPortion: number | null | undefined,
  variables: Array<{ name: string; weight: number }>,
): string {
  const parts: string[] = []
  if (fixedPortion != null && fixedPortion > 0) parts.push(`${formatPercent(fixedPortion)} fijo`)
  for (const variable of variables) parts.push(`${formatPercent(variable.weight)} ${variable.name}`)
  return parts.join(' + ')
}

// Deriva de periodLabel para heredar su guard: `formatPeriod(2026, 13)`
// devolvia "undefined 2026", que es justo lo que periodLabel no admite.
export function formatPeriod(year: number, month: number): string {
  return sentenceCase(periodLabel({ year, month }))
}

export function monthName(month: number): string {
  return MONTH_NAMES[month - 1] ?? ''
}

export function shortMonthName(month: number): string {
  return SHORT_MONTH_NAMES[month - 1] ?? ''
}

export function formatDate(date: string | Date | null): string {
  if (!date) return '-'
  const d = typeof date === 'string' ? new Date(date) : date
  const day = d.getDate()
  const month = SHORT_MONTH_NAMES[d.getMonth()]
  const year = d.getFullYear()
  return `${day} ${month} ${year}`
}

// Timezone-safe formatter for date-only strings ("YYYY-MM-DD", e.g. meeting_date).
// Avoids the UTC shift of `new Date("YYYY-MM-DD")` + getDate() in negative offsets.
export function formatDateOnly(date: string | null): string {
  if (!date) return '-'
  const [year, month, day] = date.split('-').map(Number)
  return `${day} ${SHORT_MONTH_NAMES[month - 1]} ${year}`
}

export function formatDateShortEs(date: string): string {
  const [year, month, day] = date.split('-')
  const monthAbbr = SHORT_MONTH_NAMES[parseInt(month, 10) - 1].toLowerCase()
  return `${day}-${monthAbbr}-${year}`
}

// Compact list of missing day-numbers for one period, e.g. "05, 06, 08 jul".
// Splits each ISO date manually (never `new Date`) to avoid the UTC off-by-one
// documented above formatDateOnly. Month is taken from the last (latest) date;
// the array arrives ascending and within a single calendar period.
export function formatMissingDatesList(dates: string[]): string {
  if (dates.length === 0) return ''
  const days = dates.map((date) => date.split('-')[2])
  const lastMonth = Number(dates[dates.length - 1].split('-')[1])
  const monthAbbr = SHORT_MONTH_NAMES[lastMonth - 1].toLowerCase()
  return `${days.join(', ')} ${monthAbbr}`
}

export function formatDayMonth(dateStr?: string): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CL', { day: 'numeric', month: 'short' })
}

export function actionItemAssigneeLabel(
  item: { assignee_ids?: string[]; supplier_user_assignee_ids?: string[] },
  users: Array<{ id: string; name: string }>,
  supplierUsers: Array<{ id: string; name: string }>,
): string {
  const orgNames = users
    .filter((u) => (item.assignee_ids ?? []).includes(u.id))
    .map((u) => u.name.split(' ')[0])
  const supplierNames = supplierUsers
    .filter((u) => (item.supplier_user_assignee_ids ?? []).includes(u.id))
    .map((u) => u.name.split(' ')[0])
  const names = [...orgNames, ...supplierNames]
  if (names.length === 0) return 'Sin asignar'
  if (names.length === 1) return names[0]
  if (names.length === 2) return names.join(', ')
  return `${names[0]} +${names.length - 1}`
}

// Anclajes del motor de calculo (Scores::KpiAchievementCalculator): en la meta
// el logro vale 100 y en el valor critico vale 85. No son configurables, y son
// los mismos umbrales que colorea el semaforo.
export const TARGET_ANCHOR = 100
export const CRITICAL_ANCHOR = 85

export function scoreToStatus(score: number | null | undefined): string {
  if (score == null) return 'no_data'
  if (score >= TARGET_ANCHOR) return 'green'
  if (score >= CRITICAL_ANCHOR) return 'yellow'
  return 'red'
}

export function isInformativeKpi(kpi: {
  target_value: number | string | null
  critical_value: number | string | null
}): boolean {
  return kpi.target_value === null && kpi.critical_value === null
}

export function statusColor(status: string): string {
  const entry = SEMAPHORE[status as SemaphoreStatus]
  return entry ? entry.text : SEMAPHORE.no_data.text
}

export function statusBgColor(status: string): string {
  const entry = SEMAPHORE[status as SemaphoreStatus]
  return entry ? entry.dot : SEMAPHORE.no_data.dot
}

export function objectiveTypeLabel(type: string): string {
  switch (type) {
    case 'maximize': return 'Max'
    case 'minimize': return 'Min'
    case 'target': return 'Meta'
    default: return type
  }
}

export function trendIcon(trend: string): string {
  switch (trend) {
    case 'up': return '\u2191'
    case 'down': return '\u2193'
    case 'stable': return '\u2192'
    case 'new': return '\u2022'
    default: return ''
  }
}

export function timeAgo(dateStr: string): string {
  const now = Date.now()
  const date = new Date(dateStr).getTime()
  const seconds = Math.floor((now - date) / 1000)

  if (seconds < 60) return 'hace un momento'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `hace ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `hace ${hours} ${hours === 1 ? 'hora' : 'horas'}`
  const days = Math.floor(hours / 24)
  if (days < 30) return `hace ${days} ${days === 1 ? 'día' : 'días'}`
  const months = Math.floor(days / 30)
  return `hace ${months} ${months === 1 ? 'mes' : 'meses'}`
}

export function budgetYear(budget: ContractBudget): number {
  return parseInt(budget.period_start.slice(0, 4), 10)
}
